/** Buscador de clientes por razon social o RUC (consulta al servidor por texto). */
import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { MobileIcon } from './MobileIcon';
import { useMobileClientQuery, useMobileClientsQuery } from '@/hooks/useMobileClients';
import type { MobileClientDto } from '@/services/commercialService';

export type ClientAutocompleteProps = {
  value: string | null;
  onChange: (clientId: string | null, client?: MobileClientDto) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  isRequired?: boolean;
};

export function ClientAutocomplete({
  value,
  onChange,
  label = 'Cliente',
  placeholder = 'Buscar por razón social o RUC...',
  error,
  disabled = false,
  isRequired = false,
}: ClientAutocompleteProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editing, setEditing] = useState(value === null);
  const [picked, setPicked] = useState<MobileClientDto | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searching = debouncedSearch.trim().length > 0;
  const clientsQuery = useMobileClientsQuery(debouncedSearch, searching);
  const selectedQuery = useMobileClientQuery(value);
  const results = searching ? (clientsQuery.data?.items ?? []) : [];
  const selected = picked?.id === value ? picked : (selectedQuery.data ?? null);
  const errorId = 'client-autocomplete-error';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setEditing(value === null);
    if (value === null) setPicked(null);
    if (value !== null) setSearch('');
  }, [value]);

  return (
    <div className="w-full">
      <label
        htmlFor={editing ? 'client-autocomplete-search' : undefined}
        className={clsx(
          'mb-1.5 block truncate text-sm font-medium',
          error ? 'text-error' : 'text-text-secondary',
        )}
      >
        {label}
        {isRequired && (
          <span aria-hidden="true" className="text-error">
            {' '}
            *
          </span>
        )}
      </label>

      {selected && !editing && (
        <div className="flex min-h-[52px] items-center gap-2 rounded-xl border-2 border-primary bg-primary-light px-3 py-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-light text-primary">
            <MobileIcon name="business" className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-text-primary">
              {selected.legal_name}
            </span>
            {selected.tax_id && (
              <span className="block truncate text-xs text-text-secondary">
                RUC {selected.tax_id}
              </span>
            )}
          </span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setSearch('');
              setDebouncedSearch('');
              setPicked(null);
              setEditing(true);
              onChange(null);
              requestAnimationFrame(() => inputRef.current?.focus());
            }}
            aria-label={`Cambiar ${selected.legal_name}`}
            className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-sm font-semibold text-primary hover:bg-primary-light disabled:opacity-40"
          >
            Cambiar
          </button>
        </div>
      )}

      {editing && (
        <div
          className={clsx(
            'flex min-h-[52px] items-center gap-2 rounded-xl border-2 px-3 transition-all',
            error
              ? 'border-error bg-error-light'
              : 'border-border bg-white hover:border-border-dark focus-within:border-primary focus-within:shadow-sm',
          )}
        >
          <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center text-text-tertiary">
            <MobileIcon name="search" className="h-5 w-5" />
          </span>
          <input
            ref={inputRef}
            id="client-autocomplete-search"
            type="search"
            value={search}
            disabled={disabled}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={placeholder}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className="h-12 min-h-[44px] w-full min-w-0 flex-1 bg-transparent text-base text-text-primary placeholder:text-text-tertiary focus:outline-none"
          />
          {search !== '' && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Limpiar búsqueda de cliente"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-text-tertiary hover:bg-bg-tertiary"
            >
              <MobileIcon name="close" className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {editing && searching && clientsQuery.isPending && (
        <p className="mt-2 text-sm text-text-tertiary">Buscando clientes…</p>
      )}

      {editing && searching && clientsQuery.isError && (
        <div className="mt-2 flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
          <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
          <p className="min-w-0 flex-1 break-words text-sm text-error">
            No se pudo cargar la lista de clientes.
          </p>
          <button
            type="button"
            onClick={() => void clientsQuery.refetch()}
            className="flex min-h-[44px] shrink-0 items-center rounded-lg px-3 text-sm font-semibold text-error"
          >
            Reintentar
          </button>
        </div>
      )}

      {editing && searching && !clientsQuery.isPending && !clientsQuery.isError && (
        <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto" aria-label="Clientes disponibles">
          {results.map((client) => (
            <li key={client.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  setSearch('');
                  setDebouncedSearch('');
                  setPicked(client);
                  setEditing(false);
                  onChange(client.id, client);
                }}
                className="flex min-h-[52px] w-full items-center gap-2 rounded-xl border border-border-light bg-white px-3 py-2 text-left transition-colors hover:border-border-dark hover:bg-bg-tertiary disabled:opacity-40"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-text-secondary">
                  <MobileIcon name="business" className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-text-primary">
                    {client.legal_name}
                  </span>
                  {client.tax_id && (
                    <span className="block truncate text-xs text-text-secondary">
                      RUC {client.tax_id}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
          {results.length === 0 && (
            <li className="flex flex-col items-center gap-1 rounded-card bg-white px-4 py-6 text-center shadow-card">
              <MobileIcon name="search_off" className="h-8 w-8 text-text-tertiary" />
              <p className="truncate text-sm font-semibold text-text-primary">Sin clientes</p>
              <p className="text-sm text-text-secondary">Ninguno coincide con tu búsqueda.</p>
            </li>
          )}
        </ul>
      )}

      {error && (
        <p id={errorId} className="mt-1.5 flex items-center gap-1 text-sm text-error">
          <MobileIcon name="error" className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 break-words">{error}</span>
        </p>
      )}
    </div>
  );
}
